/**
 * Zoom/pan de VISUALIZAÇÃO da mesa "Sobrepor & alinhar" (E36.F2/T02). // ux-neutrality:allow doc-comment
 *
 * Opera exclusivamente no `viewportTransform` do canvas Fabric — a lente por // ux-neutrality:allow doc-comment
 * onde se olha a cena — e NUNCA toca o objeto contestada (left/top/scale/angle // ux-neutrality:allow doc-comment
 * permanecem intactos): o `OverlapTransform` persistido não é contaminado e // ux-neutrality:allow doc-comment
 * nada da visualização vira medida (ADR-0040 §D). // ux-neutrality:allow doc-comment
 *
 * Gestos: roda = zoom centrado no cursor (mesma matemática do ZoomPanViewport); // ux-neutrality:allow doc-comment
 * pan com BARRA DE ESPAÇO + arrasto ou botão do meio — o arrasto simples segue // ux-neutrality:allow doc-comment
 * sendo o gesto de mover a contestada (sem conflito). Com espaço pressionado, // ux-neutrality:allow doc-comment
 * `skipTargetFind` desliga o hit-test dos objetos (o arrasto não pega a // ux-neutrality:allow doc-comment
 * contestada por engano). // ux-neutrality:allow doc-comment
 *
 * No jsdom dos testes o Fabric é mockado sem `setViewportTransform` — todo // ux-neutrality:allow doc-comment
 * acesso é guardado e o wiring vira no-op seguro. // ux-neutrality:allow doc-comment
 */
import type * as fabric from 'fabric';
import { clampZoom, WHEEL_ZOOM_STEP, zoomAtPoint, type ZoomPanState } from './ZoomPanViewport';

export interface OverlapViewportHandle {
  /** Substitui o estado da visualização (zoom/pan) e aplica no canvas. */
  set: (next: ZoomPanState) => void;
  /** Estado corrente (p/ data-attribute e comandos relativos). */
  get: () => ZoomPanState;
  /** Remove listeners (chamar no cleanup do effect). */
  dispose: () => void;
}

interface FabricViewportCanvas {
  setViewportTransform?: (vpt: number[]) => void;
  requestRenderAll: () => void;
  on: (event: string, handler: (opt: { e: Event }) => void) => void;
  off: (event: string, handler: (opt: { e: Event }) => void) => void;
  skipTargetFind?: boolean;
  defaultCursor?: string;
}

/**
 * Liga zoom (roda) e pan (espaço/botão-do-meio + arrasto) no canvas. `onZoom`
 * é chamado a cada mudança com o zoom corrente (espelho p/ data-attribute).
 */
export function wireOverlapViewport(
  canvas: fabric.Canvas,
  onZoom: (zoom: number) => void,
): OverlapViewportHandle {
  const c = canvas as unknown as FabricViewportCanvas;
  const view: ZoomPanState = { zoom: 1, panX: 0, panY: 0 };
  let panning = false;
  let spaceDown = false;
  let last = { x: 0, y: 0 };

  function apply(): void {
    if (typeof c.setViewportTransform === 'function') {
      c.setViewportTransform([view.zoom, 0, 0, view.zoom, view.panX, view.panY]);
      c.requestRenderAll();
    }
    onZoom(Math.round(view.zoom * 1000) / 1000);
  }

  function onWheel(opt: { e: Event }): void {
    const e = opt.e as WheelEvent;
    e.preventDefault();
    e.stopPropagation();
    const factor = e.deltaY < 0 ? WHEEL_ZOOM_STEP : 1 / WHEEL_ZOOM_STEP;
    Object.assign(view, zoomAtPoint(view, factor, e.offsetX, e.offsetY));
    apply();
  }

  function onMouseDown(opt: { e: Event }): void {
    const e = opt.e as MouseEvent;
    if (!spaceDown && e.button !== 1) return;
    e.preventDefault();
    panning = true;
    last = { x: e.clientX, y: e.clientY };
  }

  function onMouseMove(opt: { e: Event }): void {
    if (!panning) return;
    const e = opt.e as MouseEvent;
    view.panX += e.clientX - last.x;
    view.panY += e.clientY - last.y;
    last = { x: e.clientX, y: e.clientY };
    apply();
  }

  function onMouseUp(): void {
    panning = false;
  }

  function onKey(event: KeyboardEvent): void {
    if (event.code !== 'Space') return;
    // Não captura o espaço de inputs/botões focados (digitação e a11y).
    const target = event.target as HTMLElement | null;
    if (target && /^(INPUT|TEXTAREA|BUTTON|SELECT)$/.test(target.tagName)) return;
    event.preventDefault();
    spaceDown = event.type === 'keydown';
    c.skipTargetFind = spaceDown;
    c.defaultCursor = spaceDown ? 'grab' : 'default';
    if (!spaceDown) panning = false;
  }

  c.on('mouse:wheel', onWheel);
  c.on('mouse:down', onMouseDown);
  c.on('mouse:move', onMouseMove);
  c.on('mouse:up', onMouseUp);
  window.addEventListener('keydown', onKey);
  window.addEventListener('keyup', onKey);

  return {
    set: (next: ZoomPanState) => {
      view.zoom = clampZoom(next.zoom);
      view.panX = next.panX;
      view.panY = next.panY;
      apply();
    },
    get: () => ({ ...view }),
    dispose: () => {
      c.off('mouse:wheel', onWheel);
      c.off('mouse:down', onMouseDown);
      c.off('mouse:move', onMouseMove);
      c.off('mouse:up', onMouseUp);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKey);
    },
  };
}
